import React, {
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import {
    Box,
    Button,
    Alert,
    AlertTitle,
    Stack,
    Typography
} from "@mui/material";

import { useLocation } from "react-router-dom";

import GridOnIcon from "@mui/icons-material/GridOn";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ClearIcon from "@mui/icons-material/Clear";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import Header from "./Header";
import CollapsiblePanel from "./CollapsiblePanel";
import History from "./History";
import Parameters from "./Parameters";
import Splines from "./Splines";
import DataInput from "./DataInput";
import { getInitialState } from "./DefaultParameters";


const createEmptyPredictRows = (count = 5) =>
    Array.from(
        { length: count },
        () => ({
            sampleId: "",
            response1: "",
            response2: ""
        })
    );


const loadStoredCalibrationResponse = () => {

    try {

        const raw =
            localStorage.getItem(
                "microslideCalibrationResponse"
            );

        return raw
            ? JSON.parse(raw)
            : null;

    } catch {

        return null;

    }

};


// Calibration splines are persisted by CalibrationLayout.
const loadStoredCalibrationSplines = () => {

    try {

        const raw =
            localStorage.getItem(
                "microslideCalibrationSplines"
            );

        return raw
            ? JSON.parse(raw)
            : null;

    } catch {

        return null;

    }

};


/*
 * =========================================================
 * LOAD CALIBRATION ROWS
 *
 * Calibration history is stored under:
 *
 * microslideHistory_calibrate
 *
 * The value may be:
 *
 * 1. An object keyed by run
 * 2. An array of history entries
 *
 * Each entry is expected to contain:
 *
 * {
 *     run,
 *     rows
 * }
 *
 * We use the latest run.
 * =========================================================
 */

const loadStoredCalibrationRows = () => {

    try {

        const raw =
            sessionStorage.getItem(
                "microslideHistory_calibrate"
            );

        if (!raw) {
            return [];
        }

        const parsed =
            JSON.parse(raw);

        let entries = [];

        if (Array.isArray(parsed)) {

            entries = parsed;

        } else if (
            parsed &&
            typeof parsed === "object"
        ) {

            entries =
                Object.values(parsed);

        }

        if (!Array.isArray(entries) || entries.length === 0) {
            return [];
        }

        /*
         * Prefer the highest numeric run.
         * Fall back to the last stored entry.
         */
        const latestEntry =
            entries.reduce(
                (latest, entry) => {

                    if (!latest) {
                        return entry;
                    }

                    const currentRun =
                        Number(entry?.run);

                    const latestRun =
                        Number(latest?.run);

                    if (
                        Number.isFinite(currentRun) &&
                        Number.isFinite(latestRun)
                    ) {

                        return currentRun > latestRun
                            ? entry
                            : latest;

                    }

                    return entry;

                },
                null
            );

        if (
            Array.isArray(
                latestEntry?.rows
            )
        ) {

            return latestEntry.rows;

        }

        return [];

    } catch {

        return [];

    }

};


/*
 * =========================================================
 * PREDICTION LAYOUT
 * =========================================================
 */

export default function PredictionLayout() {

    const [resultsCollapsed, setResultsCollapsed] =
        useState(true);

    const [dataCollapsed, setDataCollapsed] =
        useState(false);

    const [splinesCollapsed, setSplinesCollapsed] =
        useState(true);

    const [paramsCollapsed, setParamsCollapsed] =
        useState(true);


    const [rows, setRows] =
        useState(
            createEmptyPredictRows(5)
        );


    const [parameters, setParameters] =
        useState(
            getInitialState()
        );


    const [splines, setSplines] =
        useState(() => {

            const stored =
                loadStoredCalibrationSplines();

            return stored &&
                Object.keys(stored).length > 0
                ? stored
                : {
                    preprocessing: null,
                    g1: null,
                    g2: null,
                    g3: null,
                    g4: null
                };

        });


    const location =
        useLocation();


    const [
        calibrationResponse,
        setCalibrationResponse
    ] = useState(() => {

        return loadStoredCalibrationResponse();

    });


    /*
     * =========================================================
     * CALIBRATION ROWS
     * =========================================================
     */

    const [
        calibrationRows,
        setCalibrationRows
    ] = useState(() => {

        return loadStoredCalibrationRows();

    });


    /*
     * =========================================================
     * PREDICTION HISTORY
     * =========================================================
     */

    const [
        resultDataMap,
        setResultDataMap
    ] = useState(() => {

        try {

            const saved =
                sessionStorage.getItem(
                    "microslideHistory_predict"
                );

            return saved
                ? JSON.parse(saved)
                : {};

        } catch {

            return {};

        }

    });


    /*
     * =========================================================
     * ROUTE STATE
     * =========================================================
     */

    useEffect(() => {

        const fromRoute =
            location?.state?.calibrationResponse;


        if (
            fromRoute &&
            Object.keys(fromRoute).length > 0
        ) {

            setCalibrationResponse(
                fromRoute
            );


            localStorage.setItem(
                "microslideCalibrationResponse",
                JSON.stringify(fromRoute)
            );


            /*
             * A fresh calibration invalidates
             * previous prediction results.
             */

            setRows(
                createEmptyPredictRows(5)
            );


            setResultDataMap({});


            sessionStorage.removeItem(
                "microslideHistory_predict"
            );


            setResultsCollapsed(true);

            setDataCollapsed(false);

        }


        const splinesFromRoute =
            location?.state?.calibrationSplines;


        if (
            splinesFromRoute &&
            Object.keys(splinesFromRoute).length > 0
        ) {

            setSplines(
                splinesFromRoute
            );


            localStorage.setItem(
                "microslideCalibrationSplines",
                JSON.stringify(
                    splinesFromRoute
                )
            );

        }


        /*
         * Calibration rows may be passed directly
         * from the calibration page.
         */

        const rowsFromRoute =
            location?.state?.calibrationRows;


        if (
            Array.isArray(rowsFromRoute) &&
            rowsFromRoute.length > 0
        ) {

            setCalibrationRows(
                rowsFromRoute
            );

        } else {

            /*
             * Refresh / direct navigation:
             * load the latest calibration rows
             * from sessionStorage.
             */

            const storedRows =
                loadStoredCalibrationRows();

            if (storedRows.length > 0) {

                setCalibrationRows(
                    storedRows
                );

            }

        }

    }, [location?.state]);


    /*
     * =========================================================
     * APPLY CALIBRATION A0-A3 TO PREDICTION PARAMETERS
     * =========================================================
     */

    useEffect(() => {

        if (!calibrationResponse) {
            return;
        }


        setParameters(prev => ({

            ...prev,

            fCalInterceptA0:
                String(
                    calibrationResponse.fCalInterceptA0 ??
                    prev.fCalInterceptA0 ??
                    0
                ),

            fCalSlopeA1:
                String(
                    calibrationResponse.fCalSlopeA1 ??
                    prev.fCalSlopeA1 ??
                    1
                ),

            fCalCurvatureA2:
                String(
                    calibrationResponse.fCalCurvatureA2 ??
                    prev.fCalCurvatureA2 ??
                    0
                ),

            fCalBlankCorrectionFactorA3:
                String(
                    calibrationResponse.fCalBlankCorrectionFactorA3 ??
                    prev.fCalBlankCorrectionFactorA3 ??
                    0
                )

        }));

    }, [calibrationResponse]);


    /*
     * =========================================================
     * OPEN RESULTS AFTER PREDICT
     * =========================================================
     */

    useEffect(() => {

        if (
            resultDataMap &&
            Object.keys(resultDataMap).length > 0
        ) {

            setResultsCollapsed(false);

        }

    }, [resultDataMap]);


    /*
     * =========================================================
     * LATEST PREDICTIONS
     *
     * Used by Excel/PDF exports.
     * =========================================================
     */

    const latestPredictions =
        useMemo(() => {

            const runs =
                Object.values(
                    resultDataMap || {}
                );

            if (runs.length === 0) {
                return [];
            }

            const latest =
                runs[runs.length - 1];

            const predictions =
                latest?.result?.predictions;

            return Array.isArray(predictions)
                ? predictions
                : [];

        }, [resultDataMap]);


    /*
     * =========================================================
     * CLEAR CALIBRATION
     * =========================================================
     */

    const clearCalibration = () => {

        localStorage.removeItem(
            "microslideCalibrationResponse"
        );


        setCalibrationResponse(
            null
        );


        setParameters(prev => ({

            ...prev,

            fCalInterceptA0: "0",
            fCalSlopeA1: "1",
            fCalCurvatureA2: "0",
            fCalBlankCorrectionFactorA3: "0"

        }));


        /*
         * Drop route state so a later tab switch does
         * not re-apply the old calibration.
         */

        window.history.replaceState(
            {},
            ""
        );

    };


    /*
     * =========================================================
     * EXCEL EXPORT
     * =========================================================
     */

    const exportToExcel = () => {

        if (latestPredictions.length === 0) {

            alert(
                "Run Predict first - no predictions to export."
            );

            return;

        }


        const rowsForSheet =
            latestPredictions.map(
                (prediction, index) => ({

                    Sample:
                        prediction.sampleId ||
                        index + 1,

                    Response1:
                        prediction.response1 ??
                        "",

                    Response2:
                        prediction.response2 ??
                        "",

                    Concentration:
                        prediction.concentration ??
                        "",

                    ConditionCode:
                        prediction.conditionCode ??
                        "",

                    Error:
                        prediction.error ??
                        ""

                })
            );


        const workbook =
            XLSX.utils.book_new();


        const predictionSheet =
            XLSX.utils.json_to_sheet(
                rowsForSheet
            );


        XLSX.utils.book_append_sheet(
            workbook,
            predictionSheet,
            "Predictions"
        );


        const calibrationRowsForSheet = [

            [
                "A0 (Intercept)",
                parameters.fCalInterceptA0 ?? ""
            ],

            [
                "A1 (Slope)",
                parameters.fCalSlopeA1 ?? ""
            ],

            [
                "A2 (Curvature)",
                parameters.fCalCurvatureA2 ?? ""
            ],

            [
                "A3 (Blank Correction)",
                parameters.fCalBlankCorrectionFactorA3 ?? ""
            ]

        ];


        const calibrationSheet =
            XLSX.utils.aoa_to_sheet([

                [
                    "Coefficient",
                    "Value"
                ],

                ...calibrationRowsForSheet

            ]);


        XLSX.utils.book_append_sheet(
            workbook,
            calibrationSheet,
            "Calibration"
        );


        const stamp =
            new Date()
                .toISOString()
                .replace(/[:.]/g, "-")
                .slice(0, 19);


        XLSX.writeFile(
            workbook,
            `MicroSlide_Predictions_${stamp}.xlsx`
        );

    };


    /*
     * =========================================================
     * PDF EXPORT
     * =========================================================
     */

    const exportToPdf = async () => {

        if (latestPredictions.length === 0) {

            alert(
                "Run Predict first - no predictions to export."
            );

            return;

        }


        const doc =
            new jsPDF({
                orientation: "portrait",
                unit: "pt",
                format: "letter"
            });


        doc.setFontSize(14);

        doc.text(
            "MicroSlide Prediction Report",
            40,
            40
        );


        doc.setFontSize(10);

        doc.text(
            `Generated: ${new Date().toLocaleString()}`,
            40,
            58
        );


        doc.text(
            "Active Calibration:",
            40,
            78
        );


        autoTable(
            doc,
            {

                startY: 84,

                head: [
                    [
                        "Coefficient",
                        "Value"
                    ]
                ],

                body: [

                    [
                        "A0 (Intercept)",
                        String(
                            parameters.fCalInterceptA0 ??
                            ""
                        )
                    ],

                    [
                        "A1 (Slope)",
                        String(
                            parameters.fCalSlopeA1 ??
                            ""
                        )
                    ],

                    [
                        "A2 (Curvature)",
                        String(
                            parameters.fCalCurvatureA2 ??
                            ""
                        )
                    ],

                    [
                        "A3 (Blank Correction)",
                        String(
                            parameters.fCalBlankCorrectionFactorA3 ??
                            ""
                        )
                    ]

                ],

                styles: {
                    fontSize: 9
                },

                margin: {
                    left: 40,
                    right: 40
                }

            }
        );


        doc.addPage();


        doc.setFontSize(12);

        doc.text(
            "Predictions",
            40,
            40
        );


        autoTable(
            doc,
            {

                startY: 50,

                head: [
                    [
                        "Sample",
                        "Resp1",
                        "Resp2",
                        "Concentration",
                        "Cond",
                        "Error"
                    ]
                ],

                body:
                    latestPredictions.map(
                        (prediction, index) => [

                            prediction.sampleId ||
                            index + 1,

                            prediction.response1 ??
                            "",

                            prediction.response2 ??
                            "",

                            prediction.concentration ??
                            "",

                            prediction.conditionCode ??
                            "",

                            prediction.error ??
                            ""

                        ]
                    ),

                styles: {
                    fontSize: 9
                },

                margin: {
                    left: 40,
                    right: 40
                }

            }
        );


        const stamp =
            new Date()
                .toISOString()
                .replace(/[:.]/g, "-")
                .slice(0, 19);


        doc.save(
            `MicroSlide_Predictions_${stamp}.pdf`
        );

    };


    const hasCalibration =
        !!calibrationResponse;


    /*
     * =========================================================
     * RENDER
     * =========================================================
     */

    return (

        <div>

            <Header
                mode="predict"
                rows={rows}
                parameters={parameters}
                setParameters={setParameters}
                extras={{
                    splines
                }}
                setResultDataMap={
                    setResultDataMap
                }
                onRun={() =>
                    setResultsCollapsed(false)
                }
            />


            <Box
                display="flex"
                flexDirection="column"
                padding={2}
            >

                {hasCalibration && (

                    <Alert
                        severity="success"
                        sx={{
                            mb: 2
                        }}
                        action={

                            <Button
                                size="small"
                                color="inherit"
                                startIcon={
                                    <ClearIcon />
                                }
                                onClick={
                                    clearCalibration
                                }
                            >
                                Clear
                            </Button>

                        }
                    >

                        <AlertTitle>
                            Calibration active - A0..A3 auto-applied to Predict
                        </AlertTitle>


                        <Stack
                            direction="row"
                            spacing={2}
                            flexWrap="wrap"
                        >

                            <Typography variant="body2">
                                A0 = {
                                    parameters.fCalInterceptA0
                                }
                            </Typography>

                            <Typography variant="body2">
                                A1 = {
                                    parameters.fCalSlopeA1
                                }
                            </Typography>

                            <Typography variant="body2">
                                A2 = {
                                    parameters.fCalCurvatureA2
                                }
                            </Typography>

                            <Typography variant="body2">
                                A3 = {
                                    parameters.fCalBlankCorrectionFactorA3
                                }
                            </Typography>

                        </Stack>

                    </Alert>

                )}


                {/* =================================================
                    RESULTS
                ================================================= */}

                <CollapsiblePanel
                    title="Results"
                    collapsed={resultsCollapsed}
                    onToggle={() =>
                        setResultsCollapsed(
                            !resultsCollapsed
                        )
                    }
                >

                    <History
                        resultDataMap={
                            resultDataMap
                        }

                        resultFields={[
                            "Sample Count",
                            "First Concentration",
                            "Last Concentration"
                        ]}

                        rows={[]}

                        calibrationRows={
                            calibrationRows
                        }

                        showHistory={false}

                        showCalibrationGraph={true}
                    />

                </CollapsiblePanel>


                {/* =================================================
                    SAMPLE DATA
                ================================================= */}

                <CollapsiblePanel
                    title="Sample Data (one row per sample)"
                    collapsed={dataCollapsed}
                    onToggle={() =>
                        setDataCollapsed(
                            !dataCollapsed
                        )
                    }
                >

                    <DataInput
                        columns={[
                            {
                                key: "sampleId",
                                label: "Sample ID"
                            },
                            {
                                key: "response1",
                                label: "Response 1"
                            },
                            {
                                key: "response2",
                                label: "Response 2"
                            }
                        ]}

                        rows={rows}

                        setRows={setRows}

                        placeholder={
                            "SampleID\tResp1\tResp2\n1\t0.5\t0.25\n2\t0.7\t0.35\n..."
                        }
                    />

                </CollapsiblePanel>


                {/* =================================================
                    SPLINES
                ================================================= */}

                <CollapsiblePanel
                    title="Splines"
                    collapsed={splinesCollapsed}
                    onToggle={() =>
                        setSplinesCollapsed(
                            !splinesCollapsed
                        )
                    }
                >

                    <Splines
                        splines={splines}
                        setSplines={setSplines}
                    />

                </CollapsiblePanel>


                {/* =================================================
                    ASSAY PARAMETERS
                ================================================= */}

                <CollapsiblePanel
                    title="Assay Parameters"
                    collapsed={paramsCollapsed}
                    onToggle={() =>
                        setParamsCollapsed(
                            !paramsCollapsed
                        )
                    }
                >

                    <Parameters
                        parameters={parameters}
                        setParameters={setParameters}
                    />

                </CollapsiblePanel>


                {/* =================================================
                    EXPORT
                ================================================= */}

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 1,
                        mt: 1
                    }}
                >

                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={
                            <GridOnIcon />
                        }
                        onClick={
                            exportToExcel
                        }
                        disabled={
                            latestPredictions.length === 0
                        }
                    >
                        Excel
                    </Button>


                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={
                            <PictureAsPdfIcon />
                        }
                        onClick={
                            exportToPdf
                        }
                        disabled={
                            latestPredictions.length === 0
                        }
                    >
                        PDF
                    </Button>

                </Box>

            </Box>

        </div>

    );

}


